import { useEffect, useRef } from 'react'
import { useThemeStore } from '@/store/themeStore'
import { usePreferences } from './usePreferences'
import { MODE_LIGHT, MODE_DARK } from '@/constants/theme.constant'
import type { Mode } from '@/@types/theme'

/**
 * Bridges the two parallel theme systems so a change from either side
 * propagates to the other:
 *
 *   - Zustand  themeStore.mode   (runtime state, drives .dark on <html>
 *                                 via useDarkMode, persisted to localStorage)
 *   - API      preferences.theme (cross-device, survives logout / new device)
 *
 * Without this, toggling the sun/moon in the top bar stays local and
 * picking a theme in Settings doesn't update the top-bar state.
 *
 * Behaviour:
 *   - On the first preferences load, sync Zustand from server if they
 *     disagree (server wins on hydration — that's the cross-device promise).
 *   - After hydration, every Zustand mode change fires a fire-and-forget
 *     updatePreferences. `sepia` in preferences is ignored here because
 *     Zustand's Mode is binary light/dark; sepia is a separate reading
 *     overlay handled in SettingsPage directly.
 *
 * Mount once at the app root — see Theme.tsx.
 */
export function useThemeSync() {
    const mode = useThemeStore((s) => s.mode)
    const setMode = useThemeStore((s) => s.setMode)
    const { preferences, updatePreferences } = usePreferences()
    const hydratedRef = useRef(false)

    // Server → Zustand on first load
    useEffect(() => {
        if (!preferences || hydratedRef.current) return
        const serverTheme = preferences.theme
        if (serverTheme === MODE_LIGHT || serverTheme === MODE_DARK) {
            if (serverTheme !== mode) {
                setMode(serverTheme as Mode)
            }
        }
        hydratedRef.current = true
        // Intentionally: no `mode` in deps. We only want the *first*
        // preferences arrival to push down; after that Zustand is the source.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preferences])

    // Zustand → server after hydration
    useEffect(() => {
        if (!hydratedRef.current) return
        if (!preferences) return
        if (preferences.theme === mode) return
        updatePreferences({ theme: mode })
        // Intentionally: don't depend on preferences.theme — that's server
        // state and depending on it would race the update we just fired.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode])
}
